import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { AlertDialog } from '../../../../shared/components/alert-dialog';
import type { RowLimitInstanceResult } from '../../api/data-builder-row-limit.types';

type DatasetSwitcherItemProps = {
  id: string;
  label: string;
  isActive: boolean;
  hasError?: boolean;
  rowLimitLoading?: boolean;
  rowLimitInfo?: RowLimitInstanceResult;
  onClick: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
};

export const DatasetSwitcherItem = ({
  id,
  label,
  isActive,
  hasError = false,
  rowLimitLoading,
  rowLimitInfo,
  onClick,
  onRename,
  onDelete,
}: DatasetSwitcherItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue(label);
      inputRef.current?.focus();
    }
  }, [isEditing, label]);

  const submitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed !== label) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitRename();
    }
    if (e.key === 'Escape') {
      setEditValue(label);
      setIsEditing(false);
      inputRef.current?.blur();
    }
  };

  if (isEditing) {
    return (
      <Flex
        data-testid={`data-builder-dataset-switcher-item-${id}`}
        padding="0.5rem 1rem"
        height="56px"
        align="center"
        borderTopWidth="1px"
        borderTopColor="gray.200"
        backgroundColor="white"
      >
        <Input
          ref={inputRef}
          data-testid={`data-builder-dataset-switcher-item-rename-input-${id}`}
          size="sm"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={submitRename}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          flex={1}
        />
      </Flex>
    );
  }

  const showRowLimitWarning =
    rowLimitInfo !== undefined &&
    (rowLimitInfo.severity === 'warning' || rowLimitInfo.severity === 'error');

  return (
    <Flex
      data-testid={`data-builder-dataset-switcher-item-${id}`}
      padding="0.5rem 1rem"
      minHeight="56px"
      align="center"
      cursor="pointer"
      borderTopWidth="1px"
      borderTopColor="gray.200"
      backgroundColor={isActive ? 'blue.50' : 'transparent'}
      _hover={{ backgroundColor: isActive ? 'blue.50' : 'gray.50' }}
      onClick={onClick}
      gap={2}
      role="group"
    >
      <Flex
        direction="column"
        flex={1}
        minWidth={0}
        gap={0.5}
        justifyContent="center"
      >
        <Flex gap={2} align="center">
          <Text
            color="text.primary"
            fontSize="sm"
            fontWeight={isActive ? 'semibold' : 'normal'}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {label}
            {rowLimitLoading ? (
              <Spinner
                data-testid={`data-builder-dataset-switcher-item-row-count-loading-${id}`}
                size="xs"
                color="gray.500"
                label=""
                style={{
                  marginLeft: '1rem',
                  verticalAlign: 'middle',
                  opacity: 0.3,
                }}
              />
            ) : (
              rowLimitInfo?.description && (
                <Text
                  as="span"
                  data-testid={`data-builder-dataset-switcher-item-row-count-description-${id}`}
                  fontSize="sm"
                  color={showRowLimitWarning ? 'red.500' : 'gray.500'}
                  fontWeight={showRowLimitWarning ? 'semibold' : 'normal'}
                  style={{
                    opacity: 0.7,
                    marginLeft: '0.5rem',
                  }}
                >
                  {rowLimitInfo.description}
                </Text>
              )
            )}
          </Text>
          {hasError || showRowLimitWarning ? (
            <Tooltip
              label={
                hasError
                  ? 'This dataset has errors that need to be resolved'
                  : (rowLimitInfo?.statusDescription ??
                    'Row limit exceeded for this dataset')
              }
              placement="top"
            >
              <WarningTwoIcon
                data-testid={
                  hasError
                    ? undefined
                    : `data-builder-dataset-switcher-item-row-warning-${id}`
                }
                color="red.500"
                boxSize={4}
                flexShrink={0}
                aria-hidden
              />
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>

      <Menu
        placement="bottom-end"
        offset={[0, 2]}
        onOpen={() => setIsMenuOpen(true)}
        onClose={() => setIsMenuOpen(false)}
      >
        <MenuButton
          as={IconButton}
          icon={<IconDotGrid1x3Vertical size={14} />}
          aria-label="Dataset actions"
          size="xs"
          variant="ghost"
          colorScheme="gray"
          borderRadius="4px"
          onClick={(e) => e.stopPropagation()}
          data-testid={`data-builder-dataset-switcher-item-actions-${id}`}
          opacity={isMenuOpen ? 1 : 0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.15s"
        />
        <MenuList minW={24}>
          <MenuItem
            data-testid={`data-builder-dataset-switcher-item-rename-${id}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            data-testid={`data-builder-dataset-switcher-item-delete-${id}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsDeleteConfirmOpen(true);
            }}
          >
            Delete
          </MenuItem>
        </MenuList>
      </Menu>

      {isDeleteConfirmOpen ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => setIsDeleteConfirmOpen(false)}
          headerText="Delete dataset?"
          bodyContent={
            <span>
              This dataset will be removed from your deliverable. This cannot be
              undone.
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Delete"
          onAccept={() => {
            setIsDeleteConfirmOpen(false);
            onDelete();
          }}
        />
      ) : null}
    </Flex>
  );
};
